import type {
  AnalyticsAddToCartPayload,
  AnalyticsBeginCheckoutPayload,
  AnalyticsCheckoutPayload,
  AnalyticsRemoveFromCartPayload,
  AnalyticsViewItemPayload,
} from '@jane/bloom/services/tracking/analyticsEvent';
import type {
  AddToCartEvent,
  BeginCheckoutEvent,
  PurchaseEvent,
  RemoveFromCartEvent,
  ViewItemEvent,
} from '@jane/bloom/types/google_analytics/events';
import { logError } from '@jane/bloom/utils/logError';

/**
 * Resolves the GA4 item-scoped `location_id` from a store identifier.
 *
 * GA4 recommends a Google Place ID for `location_id`, but the spec also
 * permits a custom location ID — we deliberately use Jane's internal
 * `store_id` so partners can join GA4 reports back to their other
 * Jane-keyed analytics without a Place ID lookup.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtm#purchase
 */
const toLocationId = (storeId: AnalyticsCheckoutPayload['storeId']) =>
  storeId != null && storeId !== '' ? String(storeId) : undefined;

export const purchasePayload = ({
  products,
  salesTax,
  storeId,
  storeTax,
  uuid,
}: AnalyticsCheckoutPayload): PurchaseEvent => {
  try {
    const value = products.reduce(
      (acc, { count, unit_price }) => acc + unit_price * count,
      0
    );
    const location_id = toLocationId(storeId);
    return {
      /** @todo Hardcoded! Tune for Canadian stores */
      currency: 'USD',
      items: products.map(
        ({ brand, count, name, price_id, product_id, unit_price }) => ({
          item_brand: brand ?? undefined,
          item_id: product_id.toString(),
          item_name: name,
          item_variant: price_id ?? undefined,
          ...(location_id && { location_id }),
          /** NB: unit_price is the *discounted* price, while GA4 recommends full price and discount to be set separately */
          price: unit_price,
          quantity: count,
        })
      ),
      tax: (storeTax || 0) + (salesTax || 0),
      transaction_id: uuid,
      value,
    };
  } catch (e) {
    logError(e);
    return {} as PurchaseEvent;
  }
};

export const viewItemPayload = ({
  brand,
  name,
  price_id,
  product_id,
  storeId,
}: AnalyticsViewItemPayload): ViewItemEvent => {
  try {
    const location_id = toLocationId(storeId);
    return {
      items: [
        {
          item_brand: brand ?? undefined,
          item_id: product_id.toString(),
          item_name: name,
          item_variant: price_id ?? undefined,
          ...(location_id && { location_id }),
        },
      ],
    };
  } catch (e) {
    logError(e);
    return {} as ViewItemEvent;
  }
};

export const removeFromCartPayload = ({
  productId,
  quantity,
  storeId,
}: AnalyticsRemoveFromCartPayload): RemoveFromCartEvent => {
  try {
    const location_id = toLocationId(storeId);
    return {
      items: [
        {
          item_id: productId.toString(),
          ...(location_id && { location_id }),
          ...(quantity != null && { quantity }),
        },
      ],
    };
  } catch (e) {
    logError(e);
    return {} as RemoveFromCartEvent;
  }
};

export const beginCheckoutPayload = ({
  cartProducts,
  storeId,
}: AnalyticsBeginCheckoutPayload): BeginCheckoutEvent => {
  try {
    const location_id = toLocationId(storeId);
    return {
      items: cartProducts.map(
        ({ brand, count, name, price_id, product_id }) => ({
          item_brand: brand ?? undefined,
          item_id: product_id.toString(),
          item_name: name,
          item_variant: price_id ?? undefined,
          ...(location_id && { location_id }),
          quantity: count,
        })
      ),
    };
  } catch (e) {
    logError(e);
    return {} as BeginCheckoutEvent;
  }
};

export const addToCartQuantityPayload = ({
  productId,
  quantity,
  storeId,
}: AnalyticsRemoveFromCartPayload): AddToCartEvent => {
  try {
    const location_id = toLocationId(storeId);
    return {
      items: [
        {
          item_id: productId.toString(),
          ...(location_id && { location_id }),
          ...(quantity != null && { quantity }),
        },
      ],
    };
  } catch (e) {
    logError(e);
    return {} as AddToCartEvent;
  }
};

export const addToCartPayload = ({
  product,
  productId,
  storeId,
}: AnalyticsAddToCartPayload): AddToCartEvent => {
  try {
    const { brand, name } = product;
    const location_id = toLocationId(storeId);
    return {
      items: [
        {
          item_brand: brand ?? undefined,
          item_id: productId.toString(),
          item_name: name,
          ...(location_id && { location_id }),
        },
      ],
    };
  } catch (e) {
    logError(e);
    return {} as AddToCartEvent;
  }
};
